
































































































































































































import { Component, Vue } from 'vue-property-decorator';
import { getIsPortalEnabledStatus } from '@/appsync/service/SystemSettingQueryService';
import { BusEvent } from '@/containers/globals/BusEvent';
import { i18n } from '@/plugins/i18n/i18n';
import { SYSTEM_TYPES, SystemType } from '@/services/role/roleConstants';
import {
  createRole,
  updateRole,
  getRole,
  isUserMemberOfRole,
  findExistingDefaultRole,
} from '@/services/role/roleService';
import { isSystemRole } from '@/services/role/roleUtils';
import { commonModule } from '@/store/modules/CommonStore';
import { globalSnackbarModule } from '@/store/modules/GlobalSnackbarStore';
import { LoginInfo } from '@/types/type';

@Component
export default class RoleEdit extends Vue {
  private loading = false;
  private initializing = false;
  private isFormValid = true;
  private tenant = '';
  private landscape = '';
  private loginInfo!: LoginInfo;
  private showSettingsDialog = false;
  private showDisableSettingsDialog = false;
  private disableDialogMessage = '';
  private showDefaultRoleDialog = false;
  private existingDefaultRoleName = '';
  private isCurrentUserMemberOfRole = false;
  private isSystemRoleFlag = false;

  // テンプレートから参照するためのプロパティ
  private SYSTEM_TYPES = SYSTEM_TYPES;
  private form = {
    name: '',
    description: '',
    systemType: SYSTEM_TYPES.FBS as SystemType,
    isDefault: false,
    permissions: {
      menus: {
        scheduler: false,
        job: false,
        settings: false,
        home: false,
        sitemap: false,
      },
    },
  };

  get isEditMode() {
    return !!this.$route.params.id;
  }

  get roleId() {
    return this.$route.params.id;
  }

  async created() {
    // commonModuleからログイン情報を取得
    this.loginInfo = commonModule.loginInfo;
    this.tenant = this.loginInfo.tenant;
    this.landscape = this.loginInfo.landscape;

    // isPortalEnabledでsystemTypeを自動設定
    const isPortalEnabled = await getIsPortalEnabledStatus(
      this.loginInfo.tenant,
      this.loginInfo.landscape
    );
    this.form.systemType = isPortalEnabled
      ? SYSTEM_TYPES.PORTAL
      : SYSTEM_TYPES.FBS;

    // FBSの場合は該当権限をfalse固定
    if (this.form.systemType === SYSTEM_TYPES.FBS) {
      this.form.permissions.menus.home = false;
      this.form.permissions.menus.sitemap = false;
    }

    if (this.isEditMode) {
      await this.fetchRole();
    }
  }

  async fetchRole() {
    this.initializing = true;
    try {
      const roleData = await getRole(this.roleId);

      if (!roleData) {
        globalSnackbarModule.showSnackbarAction(
          this.$t('commonSetting.roleEdit.fetchFailed') as string
        );
        this.navigateBack();
        return;
      }

      // テナント・ランドスケープのセキュリティチェック
      if (
        roleData.tenant !== this.tenant ||
        roleData.landscape !== this.landscape
      ) {
        globalSnackbarModule.showSnackbarAction(
          this.$t('commonSetting.roleEdit.noPermission') as string
        );
        this.navigateBack();
        return;
      }

      const isPortalEnabled = await getIsPortalEnabledStatus(
        this.tenant,
        this.landscape
      );

      this.form = {
        name: roleData.name,
        description: roleData.description || '',
        systemType: isPortalEnabled ? SYSTEM_TYPES.PORTAL : SYSTEM_TYPES.FBS,
        isDefault: roleData.isDefault,
        permissions: roleData.permissions || {
          menus: {
            scheduler: false,
            job: false,
            settings: false,
            home: false,
            sitemap: false,
          },
        },
      };

      // FBSの場合は該当権限をfalse固定
      if (this.form.systemType === SYSTEM_TYPES.FBS) {
        this.form.permissions.menus.home = false;
        this.form.permissions.menus.sitemap = false;
      }

      // 自分が所属しているロールかチェック
      this.isCurrentUserMemberOfRole = await isUserMemberOfRole(this.roleId);

      // システムロールかチェック
      this.isSystemRoleFlag = isSystemRole(this.roleId);
    } catch (error) {
      // console.error('Failed to fetch role:', error);
      globalSnackbarModule.showSnackbarAction(
        this.$t('commonSetting.roleEdit.fetchFailed') as string
      );

      this.navigateBack();
    } finally {
      this.initializing = false;
    }
  }

  async saveRole() {
    if (!(this.$refs.form as any).validate()) return;

    this.loading = true;
    try {
      const roleData = {
        ...this.form,
        tenant: this.tenant,
        landscape: this.landscape,
      };

      // FBSの場合は該当権限をfalse固定
      if (roleData.systemType === SYSTEM_TYPES.FBS) {
        roleData.permissions.menus.home = false;
        roleData.permissions.menus.sitemap = false;
      }

      if (this.isEditMode) {
        await updateRole({
          id: this.roleId,
          ...roleData,
        });
        globalSnackbarModule.showSnackbarAction(
          this.$t('commonSetting.roleEdit.updateSuccess') as string
        );
      } else {
        await createRole(roleData);
        globalSnackbarModule.showSnackbarAction(
          this.$t('commonSetting.roleEdit.createSuccess') as string
        );
      }
      this.navigateBack();
    } catch (error) {
      const action = this.isEditMode
        ? this.$t('commonSetting.roleEdit.update')
        : this.$t('commonSetting.roleEdit.create');
      globalSnackbarModule.showSnackbarAction(
        this.$t('commonSetting.roleEdit.saveFailed', { action }) as string
      );
    } finally {
      this.loading = false;
    }
  }

  navigateBack() {
    this.$router.push({ name: 'roles' });
  }

  cancelSettingsChange() {
    this.form.permissions.menus.settings = false;
    this.showSettingsDialog = false;
  }

  confirmSettingsChange() {
    this.showSettingsDialog = false;
  }

  cancelDisableSettingsChange() {
    this.form.permissions.menus.settings = true; // スイッチをオンに戻す
    this.showDisableSettingsDialog = false;
  }

  confirmDisableSettingsChange() {
    this.showDisableSettingsDialog = false;
  }

  async onSettingsChange(value: boolean) {
    if (value) {
      this.showSettingsDialog = true;
    } else {
      this.disableDialogMessage = this.$t(
        'commonSetting.roleEdit.disableSettingsConfirm'
      ) as string;
      this.showDisableSettingsDialog = true;
    }
  }

  confirmDisableSettings() {
    this.form.permissions.menus.settings = false; // スイッチをオフにする
    this.showDisableSettingsDialog = false;
  }

  async onIsDefaultChange(value: boolean) {
    if (value) {
      // デフォルトロールをONにする場合、既存のデフォルトロールを確認
      const existingDefaultRole = await findExistingDefaultRole(
        this.form.systemType,
        this.tenant,
        this.landscape,
        this.isEditMode ? this.roleId : null
      );

      if (existingDefaultRole) {
        this.existingDefaultRoleName = existingDefaultRole.name;
        this.showDefaultRoleDialog = true;
      }
    }
  }

  cancelDefaultRoleChange() {
    this.form.isDefault = false;
    this.showDefaultRoleDialog = false;
  }

  confirmDefaultRoleChange() {
    this.showDefaultRoleDialog = false;
  }
}
